import { useEffect, useState } from "react";
import Button from "../../../components/button";
import CardContainer from "../../../components/card_container";
import Input from "../../../components/input";
import LoadingModal from "../../../components/loading_modal";
import UseFormCreateUser from "../../../hooks/master/useCreateUser";
import InputSwitch from "../../../components/input_switch";
import { FiChevronDown, FiChevronRight, FiFolder, FiUsers, FiPackage, FiShoppingCart, FiBarChart2, FiSettings } from "react-icons/fi";
import "./adminStyle.scss";

const AddAdministratorPage = () => {
    const {
        isLoadingPut,
        form,
        handleChange,
        handleSubmit,
    } = UseFormCreateUser({
        type: "post",
    });

    // State untuk expanded/collapsed sections
    const [expandedSections, setExpandedSections] = useState({
        master: true,
        settings: true,
        transactions: true
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <>
            <CardContainer
                title={"Add Administrator"}
                subtitle="Create new administrator account with access permissions"
                isBack={true}
                children={(
                    <form className="admin-form" style={{ height: "78vh" }} onSubmit={(e) => handleSubmit(e)}>
                        <div className="form-user">
                            {/* User Information Section */}
                            <div className="user-info-section">
                                <h3 className="section-title">Account Information</h3>
                                <div className="form-grid">
                                    <Input 
                                        value={form.name} 
                                        style={{ width: "100%" }} 
                                        id="name" 
                                        onChange={handleChange} 
                                        label="Full Name" 
                                        placeholder="Enter full name" 
                                        required={true} 
                                    />
                                    <Input 
                                        value={form.email} 
                                        style={{ width: "100%" }} 
                                        id="email" 
                                        type="email" 
                                        onChange={handleChange} 
                                        label="Email Address" 
                                        placeholder="Enter email address" 
                                        required={true} 
                                    />
                                    <Input 
                                        value={form.password} 
                                        style={{ width: "100%" }} 
                                        type="password" 
                                        id="password" 
                                        onChange={handleChange} 
                                        label="Password" 
                                        placeholder="Enter password" 
                                        required={false}
                                        helperText="Leave blank to generate automatic password"
                                    />
                                    <Input 
                                        value={form.phone_number} 
                                        style={{ width: "100%" }} 
                                        type="tel" 
                                        id="phone_number" 
                                        onChange={handleChange} 
                                        label="Phone Number" 
                                        placeholder="Enter phone number" 
                                        required={true} 
                                    />
                                </div>
                            </div>

                            <hr className="section-divider" />

                            {/* Features Access Section */}
                            <div className="features-access-section">
                                <div className="section-header">
                                    <h3 className="section-title">Features Access Control</h3>
                                    <p className="section-description">
                                        Select which features this administrator can access
                                    </p>
                                </div>

                                {/* Quick Access - All Features */}
                                <div className="access-quick-toggle">
                                    <InputSwitch 
                                        id="features_access_user.all" 
                                        label={
                                            <div className="switch-label">
                                                <span className="label-text">Grant All Access</span>
                                                <span className="label-description">Enable all features and permissions</span>
                                            </div>
                                        } 
                                        isOn={form.features_access_user?.all ?? false} 
                                        onClick={(value) => { 
                                            handleChange({ 
                                                target: { 
                                                    id: 'features_access_user.all', 
                                                    type: 'checkbox', 
                                                    checked: value 
                                                } 
                                            }) 
                                        }} 
                                        size="large"
                                    />
                                </div>

                                {/* Main Features */}
                                <div className="features-list">
                                    {/* Dashboard */}
                                    <div className="feature-item main-feature">
                                        <div className="feature-header">
                                            <FiBarChart2 className="feature-icon" />
                                            <div className="feature-info">
                                                <h4 className="feature-title">Dashboard</h4>
                                                <p className="feature-description">Access to analytics and overview</p>
                                            </div>
                                            <InputSwitch 
                                                id="features_access_user.dashboard" 
                                                label="" 
                                                isOn={form.features_access_user?.dashboard ?? false} 
                                                onClick={(value) => { 
                                                    handleChange({ 
                                                        target: { 
                                                            id: 'features_access_user.dashboard', 
                                                            type: 'checkbox', 
                                                            checked: value 
                                                        } 
                                                    }) 
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Fabrics */}
                                    <div className="feature-item main-feature">
                                        <div className="feature-header">
                                            <FiFolder className="feature-icon" />
                                            <div className="feature-info">
                                                <h4 className="feature-title">Fabrics</h4>
                                                <p className="feature-description">Manage fabric inventory and details</p>
                                            </div>
                                            <InputSwitch 
                                                id="features_access_user.fabrics" 
                                                label="" 
                                                isOn={form.features_access_user?.fabrics ?? false} 
                                                onClick={(value) => { 
                                                    handleChange({ 
                                                        target: { 
                                                            id: 'features_access_user.fabrics', 
                                                            type: 'checkbox', 
                                                            checked: value 
                                                        } 
                                                    }) 
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Style Accents */}
                                    <div className="feature-item main-feature">
                                        <div className="feature-header">
                                            <FiPackage className="feature-icon" />
                                            <div className="feature-info">
                                                <h4 className="feature-title">Style Accents</h4>
                                                <p className="feature-description">Manage style and accent details</p>
                                            </div>
                                            <InputSwitch 
                                                id="features_access_user.style_accents" 
                                                label="" 
                                                isOn={form.features_access_user?.style_accents ?? false} 
                                                onClick={(value) => { 
                                                    handleChange({ 
                                                        target: { 
                                                            id: 'features_access_user.style_accents', 
                                                            type: 'checkbox', 
                                                            checked: value 
                                                        } 
                                                    }) 
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Report */}
                                    <div className="feature-item main-feature">
                                        <div className="feature-header">
                                            <FiBarChart2 className="feature-icon" />
                                            <div className="feature-info">
                                                <h4 className="feature-title">Reports</h4>
                                                <p className="feature-description">View and generate system reports</p>
                                            </div>
                                            <InputSwitch 
                                                id="features_access_user.report" 
                                                label="" 
                                                isOn={form.features_access_user?.report ?? false} 
                                                onClick={(value) => { 
                                                    handleChange({ 
                                                        target: { 
                                                            id: 'features_access_user.report', 
                                                            type: 'checkbox', 
                                                            checked: value 
                                                        } 
                                                    }) 
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Master Section - Parent Menu */}
                                    <div className="feature-item parent-feature">
                                        <div className="feature-header" onClick={() => toggleSection('master')}>
                                            <div className="feature-header-left">
                                                <FiUsers className="feature-icon parent-icon" />
                                                <div className="feature-info">
                                                    <h4 className="feature-title parent-title">Master Data</h4>
                                                    <p className="feature-description">Manage system master data</p>
                                                </div>
                                            </div>
                                            <div className="feature-header-right">
                                                {expandedSections.master ? (
                                                    <FiChevronDown className="collapse-icon" />
                                                ) : (
                                                    <FiChevronRight className="collapse-icon" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Sub Menu Items - Indented */}
                                        {expandedSections.master && (
                                            <div className="sub-features-list">
                                                <div className="sub-feature-item">
                                                    <div className="sub-feature-content">
                                                        <span className="sub-feature-dot"></span>
                                                        <span className="sub-feature-label">Administrator</span>
                                                    </div>
                                                    <InputSwitch 
                                                        id="features_access_user.master.administrator" 
                                                        label="" 
                                                        isOn={form.features_access_user?.master?.administrator ?? false} 
                                                        onClick={(value) => { 
                                                            handleChange({ 
                                                                target: { 
                                                                    id: 'features_access_user.master.administrator', 
                                                                    type: 'checkbox', 
                                                                    checked: value 
                                                                } 
                                                            }) 
                                                        }} 
                                                        size="small"
                                                    />
                                                </div>
                                                
                                                <div className="sub-feature-item">
                                                    <div className="sub-feature-content">
                                                        <span className="sub-feature-dot"></span>
                                                        <span className="sub-feature-label">User Management</span>
                                                    </div>
                                                    <InputSwitch 
                                                        id="features_access_user.master.user" 
                                                        label="" 
                                                        isOn={form.features_access_user?.master?.user ?? false} 
                                                        onClick={(value) => { 
                                                            handleChange({ 
                                                                target: { 
                                                                    id: 'features_access_user.master.user', 
                                                                    type: 'checkbox', 
                                                                    checked: value 
                                                                } 
                                                            }) 
                                                        }} 
                                                        size="small"
                                                    />
                                                </div>
                                                
                                                <div className="sub-feature-item">
                                                    <div className="sub-feature-content">
                                                        <span className="sub-feature-dot"></span>
                                                        <span className="sub-feature-label">Product Category</span>
                                                    </div>
                                                    <InputSwitch 
                                                        id="features_access_user.master.product_category" 
                                                        label="" 
                                                        isOn={form.features_access_user?.master?.product_category ?? false} 
                                                        onClick={(value) => { 
                                                            handleChange({ 
                                                                target: { 
                                                                    id: 'features_access_user.master.product_category', 
                                                                    type: 'checkbox', 
                                                                    checked: value 
                                                                } 
                                                            }) 
                                                        }} 
                                                        size="small"
                                                    />
                                                </div>
                                                
                                                <div className="sub-feature-item">
                                                    <div className="sub-feature-content">
                                                        <span className="sub-feature-dot"></span>
                                                        <span className="sub-feature-label">Product List</span>
                                                    </div>
                                                    <InputSwitch 
                                                        id="features_access_user.master.product_list" 
                                                        label="" 
                                                        isOn={form.features_access_user?.master?.product_list ?? false} 
                                                        onClick={(value) => { 
                                                            handleChange({ 
                                                                target: { 
                                                                    id: 'features_access_user.master.product_list', 
                                                                    type: 'checkbox', 
                                                                    checked: value 
                                                                } 
                                                            }) 
                                                        }} 
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Transactions */}
                                    <div className="feature-item main-feature">
                                        <div className="feature-header">
                                            <FiShoppingCart className="feature-icon"/>
                                            <div className="feature-info">
                                                <h4 className="feature-title">Transactions</h4>
                                                <p className="feature-description">Process and manage transactions</p>
                                            </div>
                                            <InputSwitch 
                                                id="features_access_user.transactions" 
                                                label="" 
                                                isOn={form.features_access_user?.transactions ?? false} 
                                                onClick={(value) => { 
                                                    handleChange({ 
                                                        target: { 
                                                            id: 'features_access_user.transactions', 
                                                            type: 'checkbox', 
                                                            checked: value 
                                                        } 
                                                    }) 
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Settings - Parent Menu */}
                                    <div className="feature-item parent-feature">
                                        <div className="feature-header" onClick={() => toggleSection('settings')}>
                                            <div className="feature-header-left">
                                                <FiSettings className="feature-icon parent-icon" />
                                                <div className="feature-info">
                                                    <h4 className="feature-title parent-title">Settings</h4>
                                                    <p className="feature-description">System configuration and settings</p>
                                                </div>
                                            </div>
                                            <div className="feature-header-right">
                                                {expandedSections.settings ? (
                                                    <FiChevronDown className="collapse-icon" />
                                                ) : (
                                                    <FiChevronRight className="collapse-icon" />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Settings akan memiliki sub menu jika diperlukan */}
                                        {expandedSections.settings && (
                                            <div className="sub-features-list">
                                                <div className="sub-feature-item">
                                                    <div className="sub-feature-content">
                                                        <span className="sub-feature-dot"></span>
                                                        <span className="sub-feature-label">General Settings</span>
                                                    </div>
                                                    <InputSwitch 
                                                        id="features_access_user.settings" 
                                                        label="" 
                                                        isOn={form.features_access_user?.settings ?? false} 
                                                        onClick={(value) => { 
                                                            handleChange({ 
                                                                target: { 
                                                                    id: 'features_access_user.settings', 
                                                                    type: 'checkbox', 
                                                                    checked: value 
                                                                } 
                                                            }) 
                                                        }} 
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <hr className="section-divider" />

                            {/* Form Actions */}
                            <div className="footer-form">
                                <div className="form-actions">
                                    <Button 
                                        type="button" 
                                        variant="outline"
                                        className="btn-outline-secondary" 
                                        style={{ marginRight: "12px" }} 
                                        title="Cancel" 
                                        onClick={() => window.history.back()} 
                                    />
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        className="btn-primary" 
                                        title="Create"
                                        isLoading={isLoadingPut}
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            />
            <LoadingModal isLoading={isLoadingPut} />
        </>
    );
};

export default AddAdministratorPage;